/**
 * Products > Pricing (WPS-162).
 *
 * Manage geozone-based pricing: material pricing rates and labor rates, each
 * keyed by (product line x geo zone x effective date). Filter via the shared
 * fly-out + search, add/edit a rate in a modal, delete with a confirm modal,
 * multi-select rows to set one price across them, or bulk-upload a spreadsheet.
 *
 * Visibility is gated by entity presence (the hooks only fetch when the tenant
 * has the pricing entity) plus the `pricing.*` permission - the page is only
 * routed/menued for tenants that have it, so it never bleeds to others.
 */

import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'
import { Plus, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import { SearchInput } from '@/shared/components/ui/search-input'
import {
  ProductFilterMenu,
  type ProductFilterFacet,
  type ProductFilterValue,
} from '@/shared/components/ui/product-filter-menu'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { BulkImportDialog } from '@/admin/components/imports/bulk-import-dialog'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import type { FormFieldMeta, SelectOption } from '@/shared/lib/forms'
import { formatDisplayValue } from '@/shared/lib/forms'
import { useFormattingContext } from '@/shared/lib/formatting'
import { toastApiError } from '@/shared/lib/api-error'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { useEntityRegisteredStatus } from '@/admin/hooks/use-entity-registration'
import { useGeoZonesAll } from '@/admin/hooks/use-geo'
import {
  MATERIAL_PRICING_ENTITY,
  useMaterialPricingFacets,
  useLaborRateFacets,
  useMaterialPricingRates,
  useCreateMaterialPricingRate,
  useUpdateMaterialPricingRate,
  useDeleteMaterialPricingRate,
  useBulkSetMaterialPrice,
  useLaborRates,
  useCreateLaborRate,
  useUpdateLaborRate,
  useDeleteLaborRate,
  useBulkSetLaborRate,
  type MaterialPricingRate,
  type LaborRate,
} from '@/admin/hooks/use-pricing'

const WORK_TYPES: SelectOption[] = [
  { value: 'R_AND_R', label: 'Remove & Replace' },
  { value: 'HAND_SEAL', label: 'Hand Sealing' },
  { value: 'NAIL_OVER', label: 'Nail Over' },
  { value: 'REPAIR', label: 'Repair' },
]

// Initial page size; the user can change it via the pagination control's
// per-page selector (PAGE_SIZE_OPTIONS = 10/20/30/50), so start on a valid one.
const DEFAULT_PAGE_SIZE = 20

// A single MONEY field drives every currency display on this page, so a raw
// decimal (`price` / `rate_per_square`) always renders in the org's currency
// and locale (via the formatting context) - never as a bare number.
const MONEY_FIELD: FormFieldMeta = {
  code_name: 'amount',
  display_name: 'Amount',
  field_type: 'MONEY',
}

/**
 * Renders a decimal string as a money amount (WPS-161: "incl. currency").
 * Defaults to the org currency; a per-row `currency` (a stored labor-rate
 * currency) overrides it so a rate shows in the currency it was saved in.
 */
function MoneyCell({
  value,
  currency: rowCurrency,
}: {
  value: string | number | null | undefined
  currency?: string | null
}) {
  const { locale, currency: orgCurrency } = useFormattingContext()
  return (
    <span className="text-[13px] tabular-nums text-[var(--ink)]">
      {formatDisplayValue(MONEY_FIELD, value, {
        locale,
        currency: rowCurrency || orgCurrency,
      })}
    </span>
  )
}

/**
 * The day before an ISO date (YYYY-MM-DD), as a local date-only string. Used to
 * derive a rate's End Date from the next-newer rate's Effective Date. Parsed
 * from numeric parts (not `new Date(iso)`) so it never drifts a day across
 * timezones.
 */
function previousDay(iso: string): string {
  if (!iso || typeof iso !== 'string') return ''
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  const dt = new Date(y, m - 1, d)
  dt.setDate(dt.getDate() - 1)
  const year = dt.getFullYear()
  const month = String(dt.getMonth() + 1).padStart(2, '0')
  const day = String(dt.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Turn a stored key like `hip_and_ridge` into a display label `Hip And Ridge`. */
function humanize(key: string): string {
  if (!key) return key
  return key
    .split(/[_\s]+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ')
}

/**
 * Options for a form SELECT, guaranteeing the row's current stored value stays
 * selectable even when it predates the option list (e.g. a legacy free-text
 * product line imported before these dropdowns existed).
 */
function withCurrentValue(
  options: SelectOption[] | undefined,
  current: string | null | undefined,
): SelectOption[] {
  const opts = options ?? []
  if (current && !opts.some((o) => o.value === current)) {
    return [...opts, { value: current, label: current }]
  }
  return opts
}

function useGeoZoneOptions(): SelectOption[] {
  const { data } = useGeoZonesAll()
  return useMemo(
    () =>
      (data?.results ?? []).map((z) => ({
        value: z.uuid,
        label: z.code ? `${z.name} (${z.code})` : z.name,
      })),
    [data?.results],
  )
}

/** Shared right-cluster: fly-out filter + search, matching the list-page UX. */
function FilterCluster({
  facets,
  filterValues,
  onFilterValuesChange,
  search,
  onSearchChange,
  searchPlaceholder,
}: {
  facets: ProductFilterFacet[]
  filterValues: ProductFilterValue
  onFilterValuesChange: (v: ProductFilterValue) => void
  search: string
  onSearchChange: (v: string) => void
  searchPlaceholder: string
}) {
  return (
    <>
      <ProductFilterMenu
        facets={facets}
        value={filterValues}
        onChange={onFilterValuesChange}
        triggerClassName="h-[34px] rounded-[6px] border-[0.5px] border-[var(--rule-strong)] px-3 text-[13px]"
      />
      <SearchInput
        placeholder={searchPlaceholder}
        value={search}
        onChange={onSearchChange}
        className="cv-data-table-body h-[34px] w-[220px] rounded-[6px] border-[0.5px] border-solid border-[var(--rule-strong)]"
      />
    </>
  )
}

// ── Material pricing rates ─────────────────────────────────────────────

function MaterialRatesSection() {
  const { can } = usePermissions()
  const { currency: orgCurrency } = useFormattingContext()
  const canEdit = can('pricing.edit')
  const canCreate = can('pricing.create')
  const canDelete = can('pricing.delete')
  const queryClient = useQueryClient()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [filterValues, setFilterValues] = useState<ProductFilterValue>({})
  const [search, setSearch] = useState('')
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<MaterialPricingRate | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<MaterialPricingRate | null>(null)
  const [setPriceOpen, setSetPriceOpen] = useState(false)
  const [showImport, setShowImport] = useState(false)

  const zoneOptions = useGeoZoneOptions()
  const { data: facetData } = useMaterialPricingFacets()

  const facets = useMemo<ProductFilterFacet[]>(
    () => [
      { key: 'geozone', label: 'Geo Zone', kind: 'field', options: zoneOptions },
      {
        key: 'product_line',
        label: 'Product Template',
        kind: 'field',
        type: 'select',
        options: (facetData?.product_lines ?? []).map((p) => ({
          value: p,
          label: humanize(p),
        })),
      },
      {
        key: 'product_base',
        label: 'Product Base',
        kind: 'field',
        type: 'select',
        options: (facetData?.product_bases ?? []).map((b) => ({
          value: b,
          label: b,
        })),
      },
      {
        key: 'effective_date',
        label: 'Effective Date',
        kind: 'field',
        type: 'date',
        options: (facetData?.effective_dates ?? []).map((d) => ({
          value: d,
          label: d,
        })),
      },
    ],
    [zoneOptions, facetData],
  )

  const params = useMemo(() => {
    const p: Record<string, string | number> = { page, page_size: pageSize }
    const zones = filterValues.geozone
    if (zones?.length) p.geozone = zones.join(',')
    const lines = filterValues.product_line
    if (lines?.length) p.product_line = lines.join(',')
    const bases = filterValues.product_base
    if (bases?.length) p.product_base = bases.join(',')
    const dates = filterValues.effective_date
    if (dates?.length) p.effective_date = dates.join(',')
    if (search.trim()) p.search = search.trim()
    return p
  }, [page, pageSize, filterValues, search])

  const { data, isLoading, isFetching } = useMaterialPricingRates(params)
  const createRate = useCreateMaterialPricingRate()
  const updateRate = useUpdateMaterialPricingRate()
  const deleteRate = useDeleteMaterialPricingRate()
  const bulkSet = useBulkSetMaterialPrice()

  const rows = data?.results ?? []
  const totalCount = data?.count ?? 0

  // Form dropdown options from the tenant's product templates / bases / UOM
  // attribute. When editing a legacy row whose stored value predates these
  // lists, keep it selectable by appending it so the dropdown still shows it.
  const productLineFieldOptions = useMemo(
    () => withCurrentValue(facetData?.product_line_options, editing?.product_line),
    [facetData, editing],
  )
  const productBaseFieldOptions = useMemo(
    () => withCurrentValue(facetData?.product_base_options, editing?.product_base),
    [facetData, editing],
  )
  const uomFieldOptions = useMemo(
    () => withCurrentValue(facetData?.uom_options, editing?.uom),
    [facetData, editing],
  )

  // Product bases grouped by their product template, so the PRODUCT BASE
  // dropdown narrows to the chosen PRODUCT TEMPLATE (cascading SELECT via
  // `depends_on` / `option_groups`). When editing a legacy row whose stored base
  // predates the catalogue, keep it selectable inside its line's group so
  // opening the modal never silently drops the value.
  const productBasesByLine = useMemo<Record<string, SelectOption[]>>(() => {
    const groups: Record<string, SelectOption[]> = {}
    for (const [line, opts] of Object.entries(facetData?.product_bases_by_line ?? {})) {
      groups[line] = [...opts]
    }
    if (editing?.product_line && editing.product_base) {
      const list = groups[editing.product_line] ?? []
      if (!list.some((o) => o.value === editing.product_base)) {
        groups[editing.product_line] = [
          ...list,
          { value: editing.product_base, label: editing.product_base },
        ]
      }
    }
    return groups
  }, [facetData, editing])

  const fields = useMemo<FormFieldMeta[]>(
    () => [
      {
        code_name: 'geozone_uuid',
        display_name: 'Geo Zone',
        field_type: 'SELECT',
        required: true,
        options: zoneOptions,
      },
      {
        code_name: 'product_line',
        display_name: 'Product Template',
        field_type: productLineFieldOptions.length ? 'SELECT' : 'TEXT',
        required: false,
        options: productLineFieldOptions,
        configuration_data: { searchable: true },
      },
      {
        code_name: 'product_base',
        display_name: 'Product Base',
        field_type: productBaseFieldOptions.length ? 'SELECT' : 'TEXT',
        required: false,
        options: productBaseFieldOptions,
        // Narrow to the picked Product Template; falls back to the full base
        // list when no template is selected.
        configuration_data: {
          searchable: true,
          depends_on: 'product_line',
          option_groups: productBasesByLine,
        },
      },
      { code_name: 'material_code', display_name: 'Material Code', field_type: 'TEXT', required: false },
      { code_name: 'sap_short_code', display_name: 'SAP Short Code', field_type: 'TEXT', required: false },
      {
        code_name: 'uom',
        display_name: 'Unit of Measure',
        field_type: uomFieldOptions.length ? 'SELECT' : 'TEXT',
        required: false,
        options: uomFieldOptions,
      },
      // MONEY (not DECIMAL) so the amount input pairs with a currency dropdown
      // sourced from the org's available currencies (WPS-162 feedback). The
      // currency is display-only - only the amount is persisted (as `price`).
      { code_name: 'price', display_name: 'Price', field_type: 'MONEY', required: true },
      { code_name: 'effective_date', display_name: 'Effective Date', field_type: 'DATE', required: true },
      { code_name: 'expires_date', display_name: 'Expires Date', field_type: 'DATE', required: false },
    ],
    [zoneOptions, productLineFieldOptions, productBaseFieldOptions, productBasesByLine, uomFieldOptions],
  )

  const values = useMemo<Record<string, unknown>>(
    () =>
      editing
        ? {
            geozone_uuid: editing.geozone_uuid,
            product_line: editing.product_line,
            product_base: editing.product_base,
            material_code: editing.material_code,
            sap_short_code: editing.sap_short_code,
            uom: editing.uom,
            price: { amount: Number(editing.price), currency: orgCurrency },
            effective_date: editing.effective_date,
            expires_date: editing.expires_date ?? '',
          }
        : { uom: 'SQ', price: { currency: orgCurrency } },
    [editing, orgCurrency],
  )

  async function handleSave(v: Record<string, unknown>) {
    // MONEY field yields `{ amount, currency }`; only the amount is persisted -
    // the currency is display-only (prices are stored in the org currency).
    const priceAmount = (v.price as { amount?: number | string } | null)?.amount
    const payload = {
      geozone_uuid: String(v.geozone_uuid),
      product_line: String(v.product_line ?? '').trim(),
      product_base: String(v.product_base ?? '').trim(),
      material_code: String(v.material_code ?? '').trim(),
      sap_short_code: String(v.sap_short_code ?? '').trim(),
      uom: String(v.uom ?? 'SQ').trim() || 'SQ',
      price: String(priceAmount ?? ''),
      effective_date: String(v.effective_date),
      expires_date: v.expires_date ? String(v.expires_date) : null,
    }
    if (editing) {
      await updateRate.mutateAsync({ uuid: editing.uuid, data: payload })
      toast.success('Pricing rate updated.')
    } else {
      await createRate.mutateAsync(payload)
      toast.success('Pricing rate created.')
    }
  }

  const columns: DataTableColumn<MaterialPricingRate>[] = useMemo(
    () => [
      {
        id: 'geozone',
        header: 'Geo Zone',
        cell: (r) => (
          <span className="text-[13px] text-[var(--ink)]">
            {r.geozone_code ? `${r.geozone_name} (${r.geozone_code})` : r.geozone_name}
          </span>
        ),
      },
      {
        id: 'product_line',
        header: 'Product Template',
        cell: (r) => <span className="text-[13px] text-[var(--ink)]">{humanize(r.product_line)}</span>,
      },
      {
        id: 'product_base',
        header: 'Product Base',
        cell: (r) => (
          <span className="text-[13px] text-[var(--ink)]">{r.product_base || '-'}</span>
        ),
      },
      {
        id: 'material_code',
        header: 'Material',
        cell: (r) => <span className="text-[13px] text-[var(--ink-2)]">{r.material_code || '-'}</span>,
      },
      {
        id: 'sap_short_code',
        header: 'SAP Short Code',
        cell: (r) => <span className="text-[13px] text-[var(--ink-2)]">{r.sap_short_code || '-'}</span>,
      },
      { id: 'uom', header: 'UoM', cell: (r) => <span className="text-[13px] text-[var(--ink-2)]">{r.uom}</span> },
      {
        id: 'price',
        header: 'Price',
        cell: (r) => <MoneyCell value={r.price} />,
      },
      {
        id: 'effective_date',
        header: 'Effective',
        cell: (r) => <span className="text-[13px] text-[var(--ink-2)]">{r.effective_date}</span>,
      },
      {
        id: 'actions',
        header: '',
        cell: (r) =>
          canEdit || canDelete ? (
            <div className="flex justify-end gap-2">
              {canEdit && (
                <Button variant="ghost" size="xs" onClick={() => { setEditing(r); setFormOpen(true) }}>
                  Edit
                </Button>
              )}
              {canDelete && (
                <Button variant="ghost" size="xs" onClick={() => setDeleting(r)}>
                  Delete
                </Button>
              )}
            </div>
          ) : null,
      },
    ],
    [canEdit, canDelete],
  )

  const selectedCount = selectedKeys.size

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13px] text-[var(--ink-3)]">
          {selectedCount > 0
            ? `${selectedCount} selected`
            : `${totalCount} material rate${totalCount !== 1 ? 's' : ''}`}
        </p>
        <div className="flex items-center gap-2">
          <FilterCluster
            facets={facets}
            filterValues={filterValues}
            onFilterValuesChange={(v) => { setFilterValues(v); setPage(1) }}
            search={search}
            onSearchChange={(v) => { setSearch(v); setPage(1) }}
            searchPlaceholder="Search product template / base / material"
          />
          {selectedCount > 0 ? (
            <>
              <Button variant="ghost" onClick={() => setSelectedKeys(new Set())}>
                Clear
              </Button>
              {canEdit && (
                <Button className="gap-2" onClick={() => setSetPriceOpen(true)}>
                  Set price
                </Button>
              )}
            </>
          ) : (
            <>
              {canCreate && (
                <Button variant="outline" className="gap-2" onClick={() => setShowImport(true)}>
                  <Upload className="h-3.5 w-3.5" />
                  Import
                </Button>
              )}
              {canCreate && (
                <Button className="gap-2" onClick={() => { setEditing(null); setFormOpen(true) }}>
                  <Plus className="h-3.5 w-3.5" />
                  Add Rate
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <DataTable
        tableId="material-pricing-rates"
        columns={columns}
        data={rows}
        isLoading={isLoading}
        isFetching={isFetching}
        selectable={canEdit}
        selectedKeys={selectedKeys}
        onSelectedKeysChange={setSelectedKeys}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
      />

      <EditFormDialog
        open={formOpen}
        onOpenChange={(open) => { setFormOpen(open); if (!open) setEditing(null) }}
        title={editing ? 'Edit Material Rate' : 'Add Material Rate'}
        fields={fields}
        values={values}
        onSave={handleSave}
        isPending={createRate.isPending || updateRate.isPending}
      />

      <EditFormDialog
        open={setPriceOpen}
        onOpenChange={setSetPriceOpen}
        title={`Set price for ${selectedCount} rate${selectedCount !== 1 ? 's' : ''}`}
        fields={[{ code_name: 'price', display_name: 'Price', field_type: 'MONEY', required: true }]}
        values={{ price: { currency: orgCurrency } }}
        isPending={bulkSet.isPending}
        onSave={async (v) => {
          const amount = (v.price as { amount?: number | string } | null)?.amount
          try {
            await bulkSet.mutateAsync({ uuids: [...selectedKeys], price: String(amount ?? '') })
            toast.success(`Updated ${selectedCount} rate${selectedCount !== 1 ? 's' : ''}.`)
            setSelectedKeys(new Set())
          } catch (err) {
            toastApiError(err, 'Failed to update rates.')
          }
        }}
      />

      <ConfirmDeleteDialog
        open={!!deleting}
        onOpenChange={(open) => { if (!open) setDeleting(null) }}
        title="Delete pricing rate?"
        description={
          deleting
            ? `This removes the ${humanize(deleting.product_line)} rate for ${deleting.geozone_name}. This cannot be undone.`
            : undefined
        }
        isPending={deleteRate.isPending}
        onConfirm={async () => {
          if (!deleting) return
          try {
            await deleteRate.mutateAsync(deleting.uuid)
            toast.success('Pricing rate deleted.')
            setDeleting(null)
          } catch (err) {
            toastApiError(err, 'Failed to delete pricing rate.')
          }
        }}
      />

      <BulkImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        target="material_pricing"
        // Show the imported rates without a reload.
        onComplete={() => queryClient.invalidateQueries({ queryKey: ['material-pricing-rates'] })}
      />
    </div>
  )
}

// ── Labor rates ────────────────────────────────────────────────────────

function LaborRatesSection() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const { currency: orgCurrency } = useFormattingContext()
  const canEdit = can('pricing.edit')
  const canCreate = can('pricing.create')
  const queryClient = useQueryClient()
  const canDelete = can('pricing.delete')

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  // WPS-161 feedback: the grid shows only the currently-effective rate per combo
  // by default (one clean row); this opt-in reveals the full dated timeline.
  const [includeHistorical, setIncludeHistorical] = useState(false)
  const [filterValues, setFilterValues] = useState<ProductFilterValue>({})
  const [search, setSearch] = useState('')
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<LaborRate | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<LaborRate | null>(null)
  const [setPriceOpen, setSetPriceOpen] = useState(false)
  const [showImport, setShowImport] = useState(false)
  // WPS-161: the AC's "won't edit the rate, add a dated entry" model. Override
  // opens a create form that reuses the row's Geozone + Work Type + Product Line
  // and writes a new effective-dated rate; History shows the dated timeline.
  const [overriding, setOverriding] = useState<LaborRate | null>(null)
  const [overrideOpen, setOverrideOpen] = useState(false)
  const [historyFor, setHistoryFor] = useState<LaborRate | null>(null)

  const zoneOptions = useGeoZoneOptions()
  const { data: facetData } = useLaborRateFacets()
  const lookupProviders = useResourceLookupProviders()

  const facets = useMemo<ProductFilterFacet[]>(
    () => [
      { key: 'geozone', label: 'Geo Zone', kind: 'field', options: zoneOptions },
      {
        key: 'work_type',
        label: 'Work Type',
        kind: 'field',
        type: 'select',
        options: facetData?.work_types ?? WORK_TYPES,
      },
      {
        key: 'product_line',
        label: 'Product Line',
        kind: 'field',
        type: 'select',
        options: (facetData?.product_lines ?? []).map((p) => ({
          value: p,
          label: humanize(p),
        })),
      },
      {
        key: 'effective_date',
        label: 'Effective Date',
        kind: 'field',
        type: 'date',
        options: (facetData?.effective_dates ?? []).map((d) => ({
          value: d,
          label: d,
        })),
      },
    ],
    [zoneOptions, facetData],
  )

  const params = useMemo(() => {
    const p: Record<string, string | number> = { page, page_size: pageSize }
    const zones = filterValues.geozone
    const workTypes = filterValues.work_type
    if (zones?.length) p.geozone = zones.join(',')
    if (workTypes?.length) p.work_type = workTypes.join(',')
    const lines = filterValues.product_line
    if (lines?.length) p.product_line = lines.join(',')
    const dates = filterValues.effective_date
    if (dates?.length) p.effective_date = dates.join(',')
    if (search.trim()) p.search = search.trim()
    if (includeHistorical) p.include_historical = 1
    return p
  }, [page, pageSize, includeHistorical, filterValues, search])

  const { data, isLoading, isFetching } = useLaborRates(params)
  const createRate = useCreateLaborRate()
  const updateRate = useUpdateLaborRate()
  const deleteRate = useDeleteLaborRate()
  const bulkSet = useBulkSetLaborRate()

  const rows = data?.results ?? []
  const totalCount = data?.count ?? 0

  const fields = useMemo<FormFieldMeta[]>(
    () => [
      {
        code_name: 'geozone_uuid',
        display_name: 'Geo Zone',
        field_type: 'SELECT',
        required: true,
        options: zoneOptions,
      },
      {
        code_name: 'work_type',
        display_name: 'Work Type',
        field_type: 'SELECT',
        required: true,
        options: WORK_TYPES,
      },
      {
        code_name: 'product_line',
        display_name: t('pricing.labor_rates.product_line', 'Product Line'),
        // Searchable select of the tenant's existing product lines (reuses the
        // shared ResourceSearchInput); allow_create keeps the free-text escape
        // hatch, and the field stays optional (WPS-161).
        field_type: 'ENTITY_SEARCH',
        required: false,
        configuration_data: {
          lookup_provider: 'product_line',
          icon: 'layers',
          allow_create: true,
        },
      },
      // MONEY (not DECIMAL) so the amount input pairs with a currency dropdown
      // sourced from the org's available currencies (WPS-161, matching material
      // rate). Both the amount and the selected currency are persisted, so a
      // rate keeps its currency across saves (multi-currency support).
      { code_name: 'rate_per_square', display_name: t('pricing.labor_rates.rate_per_square', 'Rate per Square'), field_type: 'MONEY', required: true },
      { code_name: 'note', display_name: t('pricing.labor_rates.note', 'Note'), field_type: 'TEXT', required: false },
      { code_name: 'effective_date', display_name: 'Effective Date', field_type: 'DATE', required: true },
      { code_name: 'expires_date', display_name: 'Expires Date', field_type: 'DATE', required: false },
    ],
    [zoneOptions, t],
  )

  const values = useMemo<Record<string, unknown>>(
    () =>
      editing
        ? {
            geozone_uuid: editing.geozone_uuid,
            work_type: editing.work_type,
            // ENTITY_SEARCH value is { uuid, name } | null; the stored column is
            // the plain product-line code, so wrap/unwrap it here.
            product_line: editing.product_line
              ? { uuid: editing.product_line, name: editing.product_line }
              : null,
            // Re-hydrate the stored currency (falling back to the org default
            // for pre-currency rows) so reopening the form shows the saved
            // currency, not a blank/reset dropdown (WPS-161).
            rate_per_square: { amount: Number(editing.rate_per_square), currency: editing.currency || orgCurrency },
            note: editing.note ?? '',
            effective_date: editing.effective_date,
            expires_date: editing.expires_date ?? '',
          }
        : { work_type: 'R_AND_R', rate_per_square: { currency: orgCurrency } },
    [editing, orgCurrency],
  )

  async function handleSave(v: Record<string, unknown>) {
    // MONEY field yields `{ amount, currency }`; both are persisted so the rate
    // keeps its currency across saves (WPS-161).
    const rate = v.rate_per_square as { amount?: number | string; currency?: string } | null
    const payload = {
      geozone_uuid: String(v.geozone_uuid),
      work_type: String(v.work_type),
      // ENTITY_SEARCH yields { uuid, name } (or null); persist the picked/typed
      // product-line code (the name) as the plain string the column expects.
      product_line: (
        (v.product_line as { name?: string } | null)?.name ?? ''
      ).trim(),
      rate_per_square: String(rate?.amount ?? ''),
      currency: rate?.currency ?? '',
      note: String(v.note ?? '').trim(),
      effective_date: String(v.effective_date),
      expires_date: v.expires_date ? String(v.expires_date) : null,
    }
    if (editing) {
      await updateRate.mutateAsync({ uuid: editing.uuid, data: payload })
      toast.success('Labor rate updated.')
    } else {
      await createRate.mutateAsync(payload)
      toast.success('Labor rate created.')
    }
  }

  // Override: the Geozone + Work Type + Product Line are carried from the row
  // (not editable) - the operator only picks a From Date, a new Rate, and an
  // optional Note. Saving creates a new dated row; the resolver then uses the
  // latest effective_date <= today.
  const overrideFields = useMemo<FormFieldMeta[]>(
    () => [
      { code_name: 'effective_date', display_name: t('pricing.labor_rates.from_date', 'From Date'), field_type: 'DATE', required: true },
      // MONEY so the new rate carries the same currency dropdown as the edit
      // form (WPS-161); both the amount and the currency are persisted.
      { code_name: 'rate_per_square', display_name: t('pricing.labor_rates.new_rate_per_square', 'New Rate per Square'), field_type: 'MONEY', required: true },
      { code_name: 'note', display_name: t('pricing.labor_rates.note', 'Note'), field_type: 'TEXT', required: false },
    ],
    [t],
  )

  const overrideValues = useMemo<Record<string, unknown>>(
    () =>
      overriding
        ? {
            // Local date (YYYY-MM-DD) via native Intl - toISOString() would use
            // UTC and land on the wrong day near midnight in some timezones.
            effective_date: new Date().toLocaleDateString('en-CA'),
            // Carry the row's stored currency into the override (WPS-161).
            rate_per_square: { amount: Number(overriding.rate_per_square), currency: overriding.currency || orgCurrency },
            note: '',
          }
        : {},
    [overriding, orgCurrency],
  )

  async function handleOverride(v: Record<string, unknown>) {
    if (!overriding) return
    // MONEY field yields `{ amount, currency }`; persist both (WPS-161).
    const rate = v.rate_per_square as { amount?: number | string; currency?: string } | null
    try {
      await createRate.mutateAsync({
        geozone_uuid: overriding.geozone_uuid,
        work_type: overriding.work_type,
        product_line: overriding.product_line,
        rate_per_square: String(rate?.amount ?? ''),
        currency: rate?.currency ?? '',
        note: String(v.note ?? '').trim(),
        effective_date: String(v.effective_date),
      })
      // The dialog's onOpenChange clears `overriding` on close - resetting it
      // here would blank the title/fields mid close-transition (flicker).
      toast.success(
        t('pricing.labor_rates.override_success', 'Rate overridden. The new effective rate is saved.'),
      )
    } catch (err) {
      toastApiError(
        err,
        t('pricing.labor_rates.override_error', 'Failed to override the rate. A rate for that From Date may already exist.'),
      )
      throw err
    }
  }

  const columns: DataTableColumn<LaborRate>[] = useMemo(
    () => [
      {
        id: 'geozone',
        header: 'Geo Zone',
        cell: (r) => (
          <span className="text-[13px] text-[var(--ink)]">
            {r.geozone_code ? `${r.geozone_name} (${r.geozone_code})` : r.geozone_name}
          </span>
        ),
      },
      {
        id: 'work_type',
        header: 'Work Type',
        cell: (r) => (
          <span className="text-[13px] text-[var(--ink)]">
            {WORK_TYPES.find((w) => w.value === r.work_type)?.label ?? r.work_type}
          </span>
        ),
      },
      {
        id: 'product_line',
        header: 'Product Line',
        // Blank product line means the rate applies to any product line (WPS-161).
        cell: (r) => <span className="text-[13px] text-[var(--ink-2)]">{humanize(r.product_line) || '-'}</span>,
      },
      {
        id: 'rate_per_square',
        header: 'Rate / Square',
        cell: (r) => <MoneyCell value={r.rate_per_square} currency={r.currency} />,
      },
      {
        id: 'note',
        header: t('pricing.labor_rates.note', 'Note'),
        cell: (r) => (
          <span className="text-[13px] text-[var(--ink-2)]">{r.note || '-'}</span>
        ),
      },
      {
        id: 'effective_date',
        header: 'Effective',
        cell: (r) => <span className="text-[13px] text-[var(--ink-2)]">{r.effective_date}</span>,
      },
      {
        id: 'actions',
        header: '',
        cell: (r) => (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="xs" onClick={() => setHistoryFor(r)}>
              {t('pricing.labor_rates.history', 'History')}
            </Button>
            {canCreate && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => { setOverriding(r); setOverrideOpen(true) }}
              >
                {t('pricing.labor_rates.override', 'Override')}
              </Button>
            )}
            {canEdit && (
              <Button variant="ghost" size="xs" onClick={() => { setEditing(r); setFormOpen(true) }}>
                Edit
              </Button>
            )}
            {canDelete && (
              <Button variant="ghost" size="xs" onClick={() => setDeleting(r)}>
                Delete
              </Button>
            )}
          </div>
        ),
      },
    ],
    [canCreate, canEdit, canDelete, t],
  )

  const selectedCount = selectedKeys.size

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <p className="text-[13px] text-[var(--ink-3)]">
            {selectedCount > 0
              ? `${selectedCount} selected`
              : `${totalCount} labor rate${totalCount !== 1 ? 's' : ''}`}
          </p>
          <div className="flex items-center gap-2">
            <Checkbox
              id="labor-include-historical"
              checked={includeHistorical}
              onCheckedChange={(checked) => {
                setIncludeHistorical(checked === true)
                setPage(1)
              }}
            />
            <Label
              htmlFor="labor-include-historical"
              className="cursor-pointer text-[13px] font-normal text-[var(--ink-3)]"
            >
              {t('pricing.labor_rates.include_historical', 'Include historical rates')}
            </Label>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <FilterCluster
            facets={facets}
            filterValues={filterValues}
            onFilterValuesChange={(v) => { setFilterValues(v); setPage(1) }}
            search={search}
            onSearchChange={(v) => { setSearch(v); setPage(1) }}
            searchPlaceholder={t('pricing.labor_rates.search_placeholder', 'Search geo zone / product line / work type')}
          />
          {selectedCount > 0 ? (
            <>
              <Button variant="ghost" onClick={() => setSelectedKeys(new Set())}>
                Clear
              </Button>
              {canEdit && (
                <Button className="gap-2" onClick={() => setSetPriceOpen(true)}>
                  Set rate
                </Button>
              )}
            </>
          ) : (
            <>
              {canCreate && (
                <Button variant="outline" className="gap-2" onClick={() => setShowImport(true)}>
                  <Upload className="h-3.5 w-3.5" />
                  Import
                </Button>
              )}
              {canCreate && (
                <Button className="gap-2" onClick={() => { setEditing(null); setFormOpen(true) }}>
                  <Plus className="h-3.5 w-3.5" />
                  Add Rate
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <DataTable
        tableId="labor-rates"
        columns={columns}
        data={rows}
        isLoading={isLoading}
        isFetching={isFetching}
        selectable={canEdit}
        selectedKeys={selectedKeys}
        onSelectedKeysChange={setSelectedKeys}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
      />

      <ResourceLookupContextProvider providers={lookupProviders}>
        <EditFormDialog
          open={formOpen}
          onOpenChange={(open) => { setFormOpen(open); if (!open) setEditing(null) }}
          title={editing ? 'Edit Labor Rate' : 'Add Labor Rate'}
          fields={fields}
          values={values}
          onSave={handleSave}
          isPending={createRate.isPending || updateRate.isPending}
        />
      </ResourceLookupContextProvider>

      <EditFormDialog
        open={setPriceOpen}
        onOpenChange={setSetPriceOpen}
        title={`Set rate for ${selectedCount} rate${selectedCount !== 1 ? 's' : ''}`}
        fields={[{ code_name: 'price', display_name: t('pricing.labor_rates.rate_per_square', 'Rate per Square'), field_type: 'MONEY', required: true }]}
        values={{ price: { currency: orgCurrency } }}
        isPending={bulkSet.isPending}
        onSave={async (v) => {
          // MONEY field yields `{ amount, currency }`; apply both across the
          // selected rows so a bulk update keeps the currency contract (WPS-161).
          const price = v.price as { amount?: number | string; currency?: string } | null
          try {
            await bulkSet.mutateAsync({
              uuids: [...selectedKeys],
              price: String(price?.amount ?? ''),
              currency: price?.currency ?? '',
            })
            toast.success(`Updated ${selectedCount} rate${selectedCount !== 1 ? 's' : ''}.`)
            setSelectedKeys(new Set())
          } catch (err) {
            toastApiError(err, 'Failed to update rates.')
          }
        }}
      />

      <ConfirmDeleteDialog
        open={!!deleting}
        onOpenChange={(open) => { if (!open) setDeleting(null) }}
        title="Delete labor rate?"
        description={
          deleting
            ? `This removes the ${humanize(deleting.product_line)} ${WORK_TYPES.find((w) => w.value === deleting.work_type)?.label ?? deleting.work_type} rate for ${deleting.geozone_name}. This cannot be undone.`
            : undefined
        }
        isPending={deleteRate.isPending}
        onConfirm={async () => {
          if (!deleting) return
          try {
            await deleteRate.mutateAsync(deleting.uuid)
            toast.success('Labor rate deleted.')
            setDeleting(null)
          } catch (err) {
            toastApiError(err, 'Failed to delete labor rate.')
          }
        }}
      />

      <BulkImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        target="labor_rate"
        onComplete={() => queryClient.invalidateQueries({ queryKey: ['labor-rates'] })}
      />

      <EditFormDialog
        open={overrideOpen}
        onOpenChange={(open) => { setOverrideOpen(open); if (!open) setOverriding(null) }}
        title={
          overriding
            ? `${t('pricing.labor_rates.override_rate', 'Override rate')}: ${overriding.geozone_code ? `${overriding.geozone_name} (${overriding.geozone_code})` : overriding.geozone_name} / ${WORK_TYPES.find((w) => w.value === overriding.work_type)?.label ?? overriding.work_type} / ${humanize(overriding.product_line)}`
            : t('pricing.labor_rates.override_rate', 'Override rate')
        }
        fields={overrideFields}
        values={overrideValues}
        onSave={handleOverride}
        isPending={createRate.isPending}
      />

      {historyFor && (
        <LaborRateHistoryDialog row={historyFor} onClose={() => setHistoryFor(null)} />
      )}
    </div>
  )
}

/**
 * Read-only drawer listing every dated rate for one Geozone + Work Type +
 * Product Line, newest first (WPS-161: "see the history of the labor rates").
 * Reuses the list endpoint filtered to the combo, so no new API is needed.
 */
function LaborRateHistoryDialog({
  row,
  onClose,
}: {
  row: LaborRate
  onClose: () => void
}) {
  const { t } = useTranslation()
  const params = useMemo(
    () => ({
      geozone: row.geozone_uuid,
      work_type: row.work_type,
      product_line: row.product_line,
      ordering: '-effective_date',
      // The list now defaults to active-only, so the drawer must opt into the
      // full dated timeline to show prior + scheduled rates (WPS-161).
      include_historical: 1,
      page_size: 100,
    }),
    [row],
  )
  const { data, isPending } = useLaborRates(params)
  const history = data?.results ?? []

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="w-full max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{t('pricing.labor_rates.rate_history', 'Rate history')}</DialogTitle>
          <DialogDescription>
            {row.geozone_code ? `${row.geozone_name} (${row.geozone_code})` : row.geozone_name}
            {' / '}
            {WORK_TYPES.find((w) => w.value === row.work_type)?.label ?? row.work_type}
            {' / '}
            {humanize(row.product_line)}
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <p className="py-8 text-center text-[13px] text-[var(--ink-3)]">
            {t('pricing.labor_rates.loading_history', 'Loading history...')}
          </p>
        ) : history.length === 0 ? (
          <p className="py-8 text-center text-[13px] text-[var(--ink-3)]">
            {t('pricing.labor_rates.no_rate_history', 'No rate history yet.')}
          </p>
        ) : (
          <div className="flex flex-col">
            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b-[0.5px] border-b-[var(--rule)] pb-2">
              <span className="text-[11px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
                {t('pricing.labor_rates.effective_date', 'Effective Date')}
              </span>
              <span className="min-w-[92px] text-[11px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
                {t('pricing.labor_rates.end_date', 'End Date')}
              </span>
              <span className="min-w-[64px] text-right text-[11px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
                {t('pricing.labor_rates.rate', 'Rate')}
              </span>
            </div>
            <ul className="flex flex-col divide-y divide-[var(--rule-light)]">
              {history.map((h, i) => (
                <li
                  key={h.uuid}
                  className="grid grid-cols-[1fr_auto_auto] items-center gap-4 py-3"
                >
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[13px] text-[var(--ink)]">{h.effective_date}</span>
                    {h.note ? (
                      <span className="text-[12px] text-[var(--ink-3)]">{h.note}</span>
                    ) : null}
                  </div>
                  {/* Newest row (i === 0) is in force -> "Current"; older rows end
                      the day before the next-newer rate takes effect. */}
                  <span className="min-w-[92px] text-[13px] text-[var(--ink-2)]">
                    {i === 0 ? (
                      <span className="text-[var(--green)]">
                        {t('pricing.labor_rates.current', 'Current')}
                      </span>
                    ) : (
                      previousDay(history[i - 1].effective_date)
                    )}
                  </span>
                  <span className="min-w-[64px] text-right">
                    <MoneyCell value={h.rate_per_square} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function PricingPage() {
  const { t } = useTranslation()
  // Route-level entity gate: even on a direct URL hit, the page renders only
  // for tenants that have the pricing entity - matching the tab, menu, hooks,
  // and backend `RequiresEntity` gate. Wait for the lookup to resolve before
  // deciding (never redirect on a not-yet-known "unregistered"), then redirect
  // tenants without it back to the Library so no chrome bleeds through.
  const { registered, isResolving } = useEntityRegisteredStatus(MATERIAL_PRICING_ENTITY)
  if (isResolving) return null
  if (!registered) return <Navigate to="/manage/products/library" replace />

  return (
    <div>
      <Tabs defaultValue="material">
        <TabsList variant="line" className="mb-6">
          <TabsTrigger value="material">{t('pricing.tabs.material')}</TabsTrigger>
          <TabsTrigger value="labor">{t('pricing.tabs.labor')}</TabsTrigger>
        </TabsList>
        <TabsContent value="material">
          <MaterialRatesSection />
        </TabsContent>
        <TabsContent value="labor">
          <LaborRatesSection />
        </TabsContent>
      </Tabs>
    </div>
  )
}
